import { Link } from "react-router-dom";
import { Globe2, Users, Zap, ShieldCheck, ArrowRight } from "lucide-react";

const pillars = [
  {
    icon: Globe2,
    title: "Built Across Borders",
    description:
      "Our expertise spans India, the United Kingdom, and the Gulf — treasury practitioners who have navigated multi-currency balance sheets, sovereign regulations, and cross-border capital flows firsthand.",
    color: "from-indigo-500 to-purple-600",
  },
  {
    icon: Users,
    title: "Made by Treasurers, for Treasury",
    description:
      "Every feature in Nebula was designed by people who have sat in the treasurer's chair — managing FX exposures at 11pm, chasing counterparty limits, and presenting hedge rationale to boards.",
    color: "from-emerald-500 to-teal-600",
  },
  {
    icon: Zap,
    title: "Practitioner Intelligence",
    description:
      "We replaced the patchwork of Trading terminals, Excel models, and fragmented banking portals with a single AI-powered layer that thinks the way a senior treasurer thinks.",
    color: "from-amber-500 to-orange-600",
  },
  {
    icon: ShieldCheck,
    title: "Institutional Rigour",
    description:
      "Nebula is built to the standards that institutional treasury demands — audit trails, role-based access, real-time data integrity, and compliance-ready reporting baked in from day one.",
    color: "from-pink-500 to-rose-600",
  },
];

const stats = [
  { value: "15+", label: "Years Treasury Experience" },
  { value: "3", label: "Geographies Covered" },
  { value: "7", label: "Treasury Modules" },
  { value: "1", label: "Platform. Zero Compromise." },
];

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-[#0a0b0f]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center">
              <span className="text-white font-bold text-lg">N</span>
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Nebula Finance AI</h1>
              <p className="text-xs text-gray-400 -mt-1">Treasury Intelligence Platform</p>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/about" className="px-5 py-2.5 text-indigo-400 font-medium border-b border-indigo-500">
              About Us
            </Link>
            <Link
              to="/platform"
              className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium transition-all hover:-translate-y-0.5"
            >
              Build Your Platform
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-40 pb-24 px-6 relative overflow-hidden">
        {/* Background glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-indigo-600/10 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-500/10 border border-indigo-500/20 mb-10">
            <span className="w-2 h-2 bg-indigo-400 rounded-full" />
            <span className="text-sm text-indigo-300 tracking-wide uppercase font-medium">
              Who We Are
            </span>
          </div>

          <h1 className="text-5xl md:text-6xl font-bold text-white mb-8 leading-tight">
            Treasury Expertise,
            <span className="block bg-gradient-to-r from-indigo-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent mt-2">
              Encoded into Intelligence.
            </span>
          </h1>

          <p className="text-xl text-gray-400 leading-relaxed max-w-3xl mx-auto">
            Nebula Finance AI was not built in a hackathon. It was built in treasury dealing rooms, across
            multi-currency balance sheets, through FX crises and rate cycles — by practitioners who knew
            exactly what was missing and built it themselves.
          </p>
        </div>
      </section>

      {/* Stats strip */}
      <section className="py-12 px-6 border-y border-[#2a2d3a]">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <p className="text-4xl font-bold bg-gradient-to-r from-indigo-400 to-cyan-400 bg-clip-text text-transparent mb-2">
                {s.value}
              </p>
              <p className="text-sm text-gray-500">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Manifesto */}
      <section className="py-24 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="space-y-6 text-lg text-gray-400 leading-relaxed">
            <p>
              <span className="text-white font-semibold">
                Most treasury software is built by engineers who have never managed an FX book.
              </span>{" "}
              The result is powerful technology wrapped in interfaces that don't speak treasury — tools that
              require weeks of training to do what a practitioner needs in thirty seconds.
            </p>
            <p>
              Nebula Finance AI was built the other way around. We started with the decisions — hedge or
              leave open? fix or float? extend tenure or refinance? — and built backwards into the
              technology. Every screen, every data point, every AI recommendation exists because a
              real treasury decision demanded it.
            </p>
            <p>
              <span className="text-white font-semibold">
                We are not a six-figure terminal built for trading floors. We are not another Excel add-in.
              </span>{" "}
              We are the layer that sits between raw market data and treasury judgement — synthesising,
              flagging, recommending, and reporting in the language of the people who run corporate
              treasury for a living.
            </p>
            <p>
              Our team has operated across India, the United Kingdom, and the Gulf . We understand that treasury
              is not a global monolith. It is deeply regional, deeply regulatory, and deeply human.
              Nebula is built to reflect that.
            </p>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-20 px-6 bg-[#0d0e14]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">What Sets Us Apart</h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              Four principles that run through every line of code and every product decision.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {pillars.map((p) => (
              <div key={p.title} className="nebula-card p-8 group">
                <div
                  className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${p.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}
                >
                  <p.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{p.title}</h3>
                <p className="text-gray-400 leading-relaxed text-sm">{p.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">See It in Action</h2>
          <p className="text-gray-400 mb-10">
            Explore the platform architecture or log in to your regional instance.
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <Link
              to="/platform"
              className="nebula-btn-primary flex items-center gap-2 px-8 py-4 text-lg"
            >
              Start Your Build <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/login"
              className="px-8 py-4 border border-gray-700 hover:border-indigo-500 rounded-xl text-white font-medium transition-all hover:bg-indigo-500/10"
            >
              Log In
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-[#2a2d3a]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center">
              <span className="text-white font-bold">N</span>
            </div>
            <div>
              <p className="text-white font-semibold">Nebula Finance AI</p>
              <p className="text-gray-500 text-sm">Excellence in Financial Solutions</p>
            </div>
          </div>
          <p className="text-gray-500 text-sm">© 2026 Nebula Finance AI. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
